import { _x } from '@wordpress/i18n';
import type { Hit } from 'instantsearch.js';
import { useEffect, useRef } from 'react';
import { getDateString } from '../../../../assets/utils/format';
import { useDateFilterContext } from '../context/DateFilterContext';
import ServiceListGroup from './ListGroup';
import { useWidgetOptions } from './ConfigContext';

function ServiceList(props: {
  groupedItems: { date: number; items: Hit[] }[]
}) {
  const { dateFilterValue } = useDateFilterContext()
  const { compactView, emptyText } = useWidgetOptions()
  const listRef = useRef<HTMLUListElement>(null)

  function handleKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const selected = e.currentTarget.querySelector<HTMLElement>(
        '[data-hit-id].is-active'
      )
      const index = Number.parseInt(selected?.dataset.index ?? '-1')
      if (index === -1) return
      const nextIndex = index + (e.key === 'ArrowDown' ? 1 : -1)
      const nextCard = e.currentTarget.querySelector<HTMLElement>(
        `[data-index="${nextIndex}"]`
      )
      nextCard?.focus()
    }
  }

  useEffect(() => {
    const cards = listRef.current?.querySelectorAll('[data-hit-id]')
    if (cards) {
      cards.forEach((card, index) => {
        card.setAttribute('data-index', index.toString())
      })
    }
  })

  if (props.groupedItems.length === 0) {
    const currentDate = dateFilterValue?.[1] ? new Date(dateFilterValue[1]) : new Date()
    const formattedDate = getDateString(
      currentDate,
      _x('l, dS F Y', 'Celebration List Date', 'icf')
    )
    
    // Custom text from the block settings replaces title and hint; the compact
    // view spans several weeks, so a single date line would be misleading there
    return <>
      <div className="services-list-group">
        {!compactView && <p className="title h3">{formattedDate}</p>}
        <div className="text-center">
          <p className='display-3 pt-8' style={{ whiteSpace: 'pre-line' }}>
            {emptyText || _x('No Celebrations planned for this week yet', 'Celebration Empty List Title', 'icf')}
          </p>
          {!emptyText && (
            <p>{_x('Feel free to come back later.', 'Celebration Empty List Message', 'icf')}</p>
          )}
        </div>
      </div>
    </>
  }

  return (
    <ul
      ref={listRef}
      className="services-list"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {props.groupedItems.map(({ date, items }) => (
        <li key={date}>
          <ServiceListGroup timestamp={date} items={items} />
        </li>
      ))}
    </ul>
  )
}

export default ServiceList
